import { ChakraV3 } from '@piccolohealth/ui';
import { z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedFormField } from '../../../components/forms/HookedFormField';
import { HookedPinInput } from '../../../components/forms/HookedPinInput';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { sendExceptionToSentry } from '../../../utils/errors';
import { zodResolver } from '../../../utils/zod';
import { AuthCard } from './AuthCard';
import { AuthLayout } from './AuthLayout';
import { MFAChooseMethodButton } from './MFAChooseMethodButton';

type FormValues = {
  verificationCode: string;
};

const schema: z.ZodSchema<FormValues> = z.object({
  verificationCode: z.string().length(6, 'Verification code must be 6 characters'),
});

interface Props {
  onVerifyCode: (verificationCode: string) => Promise<void>;
  onResendCode: () => Promise<void>;
}

export const PhoneVerifyCard = (props: Props) => {
  const [isResending, setIsResending] = React.useState(false);

  const methods = useForm<FormValues>({
    defaultValues: {
      verificationCode: '',
    },
    resolver: zodResolver(schema),
  });

  const onVerifyCode = async (values: FormValues) => {
    return props.onVerifyCode(values.verificationCode).catch(() => {
      methods.setError('verificationCode', {
        message: 'Invalid verification code',
      });
      methods.resetField('verificationCode', {
        keepError: true,
      });
    });
  };

  const onResendCode = async () => {
    setIsResending(true);

    try {
      await props.onResendCode();
    } catch (err) {
      switch (err.code) {
        case 'auth/captcha-check-failed':
          methods.setError('verificationCode', {
            message: 'reCAPTCHA verification failed. Please reload and try again',
          });
          break;
        case 'auth/too-many-requests':
          methods.setError('verificationCode', {
            message: 'Too many verification attempts. Please try again later',
          });
          break;
        default:
          methods.setError('verificationCode', {
            message: 'Service temporarily unavailable. Please contact support',
          });
          return sendExceptionToSentry(err);
      }
    }
    setIsResending(false);
  };

  return (
    <AuthLayout>
      <AuthCard>
        <ChakraV3.Stack gap={0}>
          <ChakraV3.Text fontWeight='semibold' fontSize='2xl'>
            Two-factor Authentication
          </ChakraV3.Text>
          <ChakraV3.Text fontSize='sm' color='secondary'>
            Enter the verification code we sent to your phone
          </ChakraV3.Text>
        </ChakraV3.Stack>
        <FormProvider {...methods}>
          <ChakraV3.Stack
            as='form'
            gap={4}
            align='center'
            onSubmit={methods.handleSubmit(onVerifyCode)}
          >
            <ChakraV3.Stack>
              <HookedFormField name='verificationCode'>
                <HookedPinInput name='verificationCode' fieldCount={6} />
              </HookedFormField>
            </ChakraV3.Stack>

            <HookedSubmitButton
              w='full'
              size='md'
              mt={4}
              disabled={false}
              loadingText='Verifying...'
            >
              Verify
            </HookedSubmitButton>
          </ChakraV3.Stack>
        </FormProvider>
        <ChakraV3.HStack w='full'>
          <MFAChooseMethodButton />
          <ChakraV3.Spacer />
          <ChakraV3.Button
            variant='plain'
            w='fit-content'
            cursor='pointer'
            onClick={onResendCode}
            loading={isResending}
            loadingText='Resending code...'
          >
            Resend code
          </ChakraV3.Button>
        </ChakraV3.HStack>
      </AuthCard>
    </AuthLayout>
  );
};
